/**
Copyright (C) 2022 by SeQuenX  - All Rights Reserved
This file is part of the ComplyVantage product development,
and is released under the "Commercial License Agreement".
You should have received a copy of the Commercial License Agreement license with
this file. If not, please write to: legal@sequenx.com, or visit www.sequenx.com
*/
import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const EcommerceServiceDetails = () => {
  const { t } = useTranslation();

  const platforms = [
    {
      title: t("ecommerce.platforms.magento.title"),
      description: t("ecommerce.platforms.magento.description"),
      features: t("ecommerce.platforms.magento.features", {
        returnObjects: true,
      }),
      icon: "🛍️",
      color: "from-slate-700 to-slate-800",
    },
    {
      title: t("ecommerce.platforms.woocommerce.title"),
      description: t("ecommerce.platforms.woocommerce.description"),
      features: t("ecommerce.platforms.woocommerce.features", {
        returnObjects: true,
      }),
      icon: "🛒",
      color: "from-slate-600 to-slate-700",
    },
    {
      title: t("ecommerce.platforms.shopify.title"),
      description: t("ecommerce.platforms.shopify.description"),
      features: t("ecommerce.platforms.shopify.features", {
        returnObjects: true,
      }),
      icon: "🏪",
      color: "from-slate-800 to-slate-900",
    },
    {
      title: t("ecommerce.platforms.wordpress.title"),
      description: t("ecommerce.platforms.wordpress.description"),
      features: t("ecommerce.platforms.wordpress.features", {
        returnObjects: true,
      }),
      icon: "🌐",
      color: "from-slate-700 to-slate-800",
    },
    {
      title: t("ecommerce.platforms.webflow.title"),
      description: t("ecommerce.platforms.webflow.description"),
      features: t("ecommerce.platforms.webflow.features", {
        returnObjects: true,
      }),
      icon: "🎨",
      color: "from-slate-600 to-slate-700",
    },
    {
      title: t("ecommerce.platforms.erp.title"),
      description: t("ecommerce.platforms.erp.description"),
      features: t("ecommerce.platforms.erp.features", {
        returnObjects: true,
      }),
      icon: "🔄",
      color: "from-slate-800 to-slate-900",
    },
  ];

  const services = [
    {
      title: t("ecommerce.services.custom.title"),
      description: t("ecommerce.services.custom.description"),
      color: "from-slate-700 to-slate-800",
    },
    {
      title: t("ecommerce.services.migration.title"),
      description: t("ecommerce.services.migration.description"),
      color: "from-slate-600 to-slate-700",
    },
    {
      title: t("ecommerce.services.integration.title"),
      description: t("ecommerce.services.integration.description"),
      color: "from-slate-800 to-slate-900",
    },
    {
      title: t("ecommerce.services.performance.title"),
      description: t("ecommerce.services.performance.description"),
      color: "from-slate-700 to-slate-800",
    },
    {
      title: t("ecommerce.services.security.title"),
      description: t("ecommerce.services.security.description"),
      color: "from-slate-600 to-slate-700",
    },
    {
      title: t("ecommerce.services.support.title"),
      description: t("ecommerce.services.support.description"),
      color: "from-slate-800 to-slate-900",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            {t("ecommerce.title")}
          </h1>
          <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            {t("ecommerce.hero.description")}
          </p>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mt-4">
            {t("ecommerce.hero.subDescription")}
          </p>
        </motion.div>

        {/* Platforms Section */}
        <div className="py-16">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-12">
            {t("ecommerce.platforms.title")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {platforms.map((platform, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`bg-gradient-to-br ${platform.color} rounded-lg p-6 shadow-lg hover:shadow-xl transition-all duration-300`}
              >
                <div className="text-4xl mb-4">{platform.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {platform.title}
                </h3>
                <p className="text-gray-200 mb-4">{platform.description}</p>
                <ul className="space-y-2">
                  {platform.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-200">
                      <svg
                        className="w-4 h-4 mr-2 text-gray-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Services Section */}
        <div className="py-16 bg-gray-50 rounded-lg">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              {t("ecommerce.services.title")}
            </h2>
            <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-gray-700 max-w-2xl mx-auto">
              {t("ecommerce.services.description")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 px-4 mx-auto">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className={`bg-gradient-to-br ${service.color} rounded-lg p-6 shadow-lg hover:shadow-xl transition-all duration-300`}
              >
                <h3 className="text-xl font-semibold text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-200">{service.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <motion.a
              href="/contact-us"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-300"
            >
              {t("digitalMarketing.cta.button")}
            </motion.a>
          </div>
        </div>

        {/* Contact Section */}
        <div className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-r from-slate-700 to-slate-800 rounded-lg p-8 text-center"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              {t("ecommerce.cta.title")}
            </h2>
            <p className="text-gray-200 max-w-2xl mx-auto mb-6">
              {t("ecommerce.cta.description")}
            </p>
            <motion.a
              href="/contact-us"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-slate-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300"
            >
              {t("ecommerce.cta.button")}
            </motion.a>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default EcommerceServiceDetails;
